import React, { useState } from "react";
import {
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HtmlHelpers } from "../constants/all-constant";
import { AppConfigurationHelper } from "../../services/AppConfigurationHelper";
import {
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import Heading from "./Heading";
import moment from "moment";
import { chartBandColors } from "../../components/student/Vitals/VitalCharts";

export interface IChartArea {
  dataKey: string;
  color: string;
}
export interface IChartLine {
  dataKey: string;
  color: string;
  label: string;
}

export interface IVitalChartProps {
  chartTitle: string;
  cssStyle: any;
  chartData: any;
  XAxisKey: string;
  YAxisLabelValue: string;
  primaryLine: IChartLine;
  secondaryLine?: IChartLine;
  configKey: string;
  labelColor: string;
  labelFontSize: string;
  axisFontSize: string;
}

export const ChartRangeContent = ({ configKey }: { configKey: string }) => {
  return (
    <div className="chart-range">
      Range:{HtmlHelpers.DOUBLE_SPACE}
      {/* <span style={{ backgroundColor: chartBandColors.Normal }}></span>
      {HtmlHelpers.DOUBLE_SPACE}Normal
      {HtmlHelpers.FOUR_TIMES_SPACE} */}
      <span style={{ backgroundColor: chartBandColors.CautionHigh }}></span>{" "}
      {HtmlHelpers.DOUBLE_SPACE}Clinical Review Criteria
      {configKey !== "temperature" && (
        <>
          {HtmlHelpers.FOUR_TIMES_SPACE}
          <span style={{ backgroundColor: chartBandColors.UrgentHigh }}></span>
          {HtmlHelpers.DOUBLE_SPACE}Rapid Response Criteria
          {HtmlHelpers.DOUBLE_SPACE}
        </>
      )}
    </div>
  );
};

export const getReferenceBands = (
  chartProps: any,
  chartsConfig: any,
  YMax: number,
  YMin: number
) => {
  const { x, width } = chartProps;
  let lastHeight = 0;
  const bands = Object.keys(chartsConfig.Range).map(
    (band: any, index: number) => {
      const calcHight =
        (chartProps.height *
          (chartsConfig.Range[band].values[1] -
            chartsConfig.Range[band].values[0])) /
        (YMax - YMin);
      const currentY = chartProps.y + lastHeight;
      lastHeight = lastHeight + calcHight;
      return {
        color: chartBandColors[`${band}`],
        y: currentY,
        height: calcHight,
      };
    }
  );

  return (
    <g>
      {bands.map(({ color, y, height }: any, index: any) => (
        <rect
          key={index}
          x={x - AppConfigurationHelper.Instance().chartsXAxisPadding}
          y={y}
          width={
            width + 2 * AppConfigurationHelper.Instance().chartsXAxisPadding
          }
          height={height}
          fill={color}
          fillOpacity={1}
        />
      ))}
    </g>
  );
};

export const getYMin = (chartsConfig: any) => {
  return chartsConfig.Range[
    Object.keys(chartsConfig.Range).slice(
      Object.keys(chartsConfig.Range).length - 1,
      Object.keys(chartsConfig.Range).length
    )[0]
  ].values[0];
};
export const getYMax = (chartsConfig: any) => {
  return chartsConfig.Range[Object.keys(chartsConfig.Range).slice(0, 1)[0]]
    ?.values[1];
};

export const getCustomAxisLabel = (
  axisProps: any,
  labelColor: string,
  axisFontSize: string
) => {
  const { x, y, stroke, payload }: any = axisProps;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dx={0}
        dy={16}
        fontSize={axisFontSize}
        textAnchor="middle"
        fill={labelColor}
      >
        O{payload.index + 1}
      </text>
    </g>
  );
};

export const getCustomTooltip = (tooltipProps: any) => {
  const { active, payload, label } = tooltipProps;
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="intro">
          <b>{payload[1].value}</b> (
          {moment(payload[0].value, "DD-MM-YY HH:mm").format(
            "DD/MM/YYYY HH:mm"
          )}
          )
        </p>
      </div>
    );
  }

  return null;
};

export const getChartHeight = (tickCount: number) => tickCount * 20 + 50;

export const VitalChart = (props: IVitalChartProps) => {
  const [selectedRange, setSelectedRange] = useState(1);
  const [filteredChartData, setFilteredChartData] = useState(props.chartData);
  React.useEffect(() => {
    const filteredData =
      selectedRange === -1
        ? props.chartData
        : props.chartData.filter((x: any) => {
            return moment(x.Date, "DD-MM-YYYY HH:mm").isAfter(
              moment().startOf("D").add(-selectedRange, "day")
            );
          });

    setFilteredChartData(filteredData);
  }, [selectedRange, props.chartData]);

  const chartsConfig: any =
    AppConfigurationHelper.Instance().chartsConfig[props.configKey];
  const YMin = getYMin(chartsConfig);
  const YMax = getYMax(chartsConfig);

  const ReferenceBands = (chartProps: any) => {
    return getReferenceBands(chartProps, chartsConfig, YMax, YMin);
  };

  const CustomAxisLabel = (axisProps: any) => {
    return getCustomAxisLabel(axisProps, props.labelColor, props.axisFontSize);
  };

  const CustomTooltip = (tooltipProps: any) => {
    return getCustomTooltip(tooltipProps);
  };

  const chartHeight = getChartHeight(chartsConfig.tickCount);
  return (
    <Grid container spacing={0} className="vital-chart">
      <Grid size={8.5}>
        <Heading
          text={props.chartTitle}
          isBold={true}
          type={"h5"}
          classes="text-ellipsis"
        />
      </Grid>
      <Grid size={3.5}>
        <FormControl fullWidth>
          <InputLabel id={`label-${props.configKey}`}>
            Day Range Filter
          </InputLabel>
          <Select
            variant="outlined"
            labelId={`label-${props.configKey}`}
            value={`${selectedRange}`}
            fullWidth
            label="Day Range Filter"
            onChange={(event: SelectChangeEvent) => {
              setSelectedRange(Number(event.target.value));
            }}
          >
            <MenuItem value={-1}>All records</MenuItem>
            <MenuItem value={1}>Last one day</MenuItem>
            <MenuItem value={2}>Last 2 days</MenuItem>
            <MenuItem value={4}>Last 4 days</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid style={props.cssStyle} size={12}>
        <ChartRangeContent configKey={props.configKey} />
        <ResponsiveContainer height={chartHeight}>
          <ScatterChart
            data={filteredChartData}
            margin={{
              top: 5,
              right: 15,
              left: 15,
              bottom: 35,
            }}
          >
            <XAxis
              style={{ fill: props.labelColor }}
              dataKey={props.XAxisKey}
              padding={{
                left: AppConfigurationHelper.Instance().chartsXAxisPadding,
                right: AppConfigurationHelper.Instance().chartsXAxisPadding,
              }}
              label={{
                style: { textAnchor: "middle", fontSize: props.labelFontSize },
                value: "Observations",
                position: "bottom",
                textAnchor: "middle",
                fill: props.labelColor,
              }}
              tick={<CustomAxisLabel />}
            />
            <YAxis
              domain={[YMin, YMax]}
              tickCount={chartsConfig.tickCount}
              interval={chartsConfig.interval}
              style={{ fill: props.labelColor }}
              tick={{ fontSize: props.axisFontSize }}
              label={{
                style: { textAnchor: "middle", fontSize: props.labelFontSize },
                value: props.YAxisLabelValue,
                angle: -90,
                position: "insideLeft",
                textAnchor: "middle",
                fill: props.labelColor,
              }}
            />
            <Tooltip cursor={false} content={CustomTooltip} />
            <ReferenceArea shape={<ReferenceBands />} />
            {/* <CartesianGrid strokeDasharray="3 3" /> */}
            (props.primaryLine &&
            <Scatter
              line={true}
              shape="square"
              dataKey={props.primaryLine.dataKey}
              name={props.primaryLine.label}
              stroke={props.primaryLine.color}
              fill={props.primaryLine.color}
              strokeWidth={2}
              legendType="square"
            />
            ) (props.secondaryLine &&
            <Scatter
              line={true}
              shape="square"
              dataKey={props.secondaryLine?.dataKey}
              name={props.secondaryLine?.label}
              stroke={props.secondaryLine?.color}
              fill={props.secondaryLine?.color}
              strokeWidth={2}
              legendType="square"
            />
            )
          </ScatterChart>
        </ResponsiveContainer>
      </Grid>
    </Grid>
  );
};
